import React from 'react';
import { cn } from '../../lib/utils';
import { Mail, Phone, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import logoImg from '../../assets/images/logo.svg';

type StickyFooterProps = React.ComponentProps<'footer'>;

export function StickyFooter({ className, ...props }: StickyFooterProps) {
  return (
    <footer
      className={cn('relative w-full', className)}
      style={{ backgroundColor: '#0a0a0a' }}
      {...props}
    >
      <div className="w-full">
        <div className="relative flex flex-col justify-between gap-5 border-t border-zinc-800 px-4 py-8 md:px-12">

          <div className="relative z-10 mt-6 flex flex-col gap-8 md:flex-row xl:mt-0">
            {/* Brand column */}
            <AnimatedContainer className="w-full max-w-sm min-w-[250px] space-y-4">
              <img src={logoImg} alt="Apertai" style={{ width: '120px' }} />
              <p className="text-zinc-500 mt-6 text-sm leading-relaxed">
                Câmeras inteligentes para arenas esportivas. Gravação Full HD, transmissões ao vivo e tecnologia de ponta
                para transformar a experiência na quadra.
              </p>
              <div className="flex gap-2 pt-2">
                {socialLinks.map((link) => (
                  <a
                    key={link.title}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-8 w-8 rounded-lg border border-zinc-800 bg-zinc-900 flex items-center justify-center text-zinc-500 hover:text-orange-400 hover:border-orange-500/30 transition-colors no-underline"
                  >
                    <link.icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </AnimatedContainer>

            {/* Link groups */}
            {footerLinkGroups.map((group, index) => (
              <AnimatedContainer key={group.label} delay={0.1 + index * 0.1} className="w-full">
                <div className="mb-10 md:mb-0">
                  <h3 className="text-sm uppercase text-white font-semibold tracking-wider">{group.label}</h3>
                  <ul className="text-zinc-500 mt-4 space-y-2.5 text-sm list-none p-0 m-0">
                    {group.links.map((link) => (
                      <li key={link.title}>
                        {link.external ? (
                          <a
                            href={link.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-orange-400 inline-flex items-center transition-colors duration-300 no-underline text-zinc-500"
                          >
                            {link.icon && <link.icon className="mr-1.5 h-3.5 w-3.5" />}
                            {link.title}
                          </a>
                        ) : (
                          <Link
                            to={link.href}
                            className="hover:text-orange-400 inline-flex items-center transition-colors duration-300 no-underline text-zinc-500"
                          >
                            {link.icon && <link.icon className="mr-1.5 h-3.5 w-3.5" />}
                            {link.title}
                          </Link>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </AnimatedContainer>
            ))}
          </div>

          {/* Bottom bar */}
          <div className="relative z-10 text-zinc-600 flex flex-col items-center justify-between gap-2 border-t border-zinc-800 pt-4 text-sm md:flex-row">
            <p>© {new Date().getFullYear()} ApertAI. Todos os direitos reservados.</p>
            <p className="text-zinc-700">Feito com tecnologia Apertai</p>
          </div>
        </div>
      </div>
    </footer>
  );
}

const InstagramIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
  </svg>
);

const YoutubeIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17" />
    <path d="m10 15 5-3-5-3z" />
  </svg>
);

const socialLinks = [
  { title: 'Instagram', href: 'https://instagram.com/apertai', icon: InstagramIcon },
  { title: 'Youtube', href: 'https://youtube.com/@apertai', icon: YoutubeIcon },
];

interface FooterLink {
  title: string;
  href: string;
  icon?: React.ComponentType<{ className?: string }>;
  external?: boolean;
}

const footerLinkGroups: { label: string; links: FooterLink[] }[] = [
  {
    label: 'Plataforma',
    links: [
      { title: 'Ver Vídeos', href: '/videos' },
      { title: 'Transmissões ao Vivo', href: '/livestream' },
      { title: 'Contratar Câmera', href: '/contract-camera' },
      { title: 'Login', href: '/login' },
      { title: 'Cadastro', href: '/signup' },
    ],
  },
  {
    label: 'Contato',
    links: [
      { title: 'apertai.adm@gmail.com', href: 'mailto:apertai.adm@gmail.com', icon: Mail, external: true },
      { title: '(31) 99204-8792', href: 'https://wa.me/5531992048792', icon: Phone, external: true },
      { title: 'Belo Horizonte, MG', href: '#', icon: MapPin },
    ],
  },
  {
    label: 'Legal',
    links: [
      { title: 'Termos de Uso', href: '/termos-de-uso.pdf', external: true },
      { title: 'Termos de Privacidade', href: '/' },
      { title: 'Cookies', href: '/' },
    ],
  },
];

function AnimatedContainer({ children, className }: { children?: React.ReactNode; delay?: number; className?: string }) {
  return <div className={className}>{children}</div>;
}
